import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, AlertTriangle, RefreshCw, CheckCircle, Trophy, Zap, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PromptGolfGame = ({ onComplete }) => {
  const [promptScore, setPromptScore] = useState(10); // Base score (The "Lazy" prompt)
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [feedback, setFeedback] = useState("AI Status: Waiting for context...");

  // --- THE BLOCK BANK ---
  // Strategy: 1 Perfect Option, 1 "Okay" Option (Distractor), 1 "Bad" Option (Trap) per category.
  const BLOCK_OPTIONS = [
    // 1. ROLE (Who is the AI?)
    { id: 'role_pro', category: 'Role', label: "👑 Role: Sr. HR Manager", value: 25, type: 'good', tip: "Perfect. Establishes authority and perspective." },
    { id: 'role_ok', category: 'Role', label: "👤 Role: Professional", value: 10, type: 'meh', tip: "Too vague. 'Professional' what? Lawyer? Doctor?" },
    { id: 'role_bad', category: 'Role', label: "🤖 Role: Corporate Bot", value: -10, type: 'bad', tip: "Result will sound cold and robotic." },

    // 2. CONTEXT (The Situation)
    { id: 'ctx_pro', category: 'Context', label: "📝 Context: 2nd Round Interview Candidate", value: 25, type: 'good', tip: "Crucial. Tells the AI this is a high-stakes relationship." },
    { id: 'ctx_ok', category: 'Context', label: "📝 Context: They applied", value: 5, type: 'meh', tip: "Obvious information. Doesn't add nuance." },
    
    // 3. TONE (The Vibe)
    { id: 'tone_pro', category: 'Tone', label: "🎭 Tone: Firm but Empathetic", value: 20, type: 'good', tip: "The Goldilocks zone. Respectful rejection." },
    { id: 'tone_trap', category: 'Tone', label: "🙏 Tone: Super Apologetic", value: 5, type: 'meh', tip: "Trap! Over-apologizing opens you up to liability and arguments." },
    { id: 'tone_bad', category: 'Tone', label: "⚡ Tone: Brutally Honest", value: -20, type: 'bad', tip: "HR Nightmare. Never be 'brutal' in writing." },

    // 4. CONSTRAINTS (The Guardrails)
    { id: 'con_pro', category: 'Format', label: "📏 Limit: Max 150 words, keep door open", value: 20, type: 'good', tip: "Great constraints prevents rambling." },
    { id: 'con_bad', category: 'Format', label: "📜 Format: Formal Legal Letter", value: -5, type: 'bad', tip: "Too stiff. We want a relationship, not a lawsuit." },
    { id: 'con_lazy', category: 'Format', label: "🚀 Speed: Write it ASAP", value: -10, type: 'bad', tip: "'ASAP' usually makes the AI hallucinate details to be fast." }
  ];

  // --- DYNAMIC SIMULATION ENGINE ---
  // Generates the text output based on score tiers + specific flags
  const getSimulatedOutput = (score, blocks) => {
    const hasIds = (id) => blocks.some(b => b.id === id);

    // 1. THE TRAPS (Specific bad combinations)
    if (hasIds('tone_bad')) return "AI: 'Listen, you weren't good enough. Stop wasting our time. Good luck elsewhere.' (Oof. You just got the company sued.)";
    if (hasIds('tone_trap')) return "AI: 'I am so so sorry! We feel terrible! Please forgive us! You are amazing but we messed up!' (Too weak. Candidates will argue with this.)";
    if (hasIds('role_bad')) return "AI: 'REJECTION_PROTOCOL_INITIATED. CANDIDATE_ID_404. TERMINATING PROCESS.' (Robotic and cold.)";

    // 2. SCORE TIERS
    if (score < 30) return "AI: 'No thanks. We hired someone else. Bye.' (Too blunt. Missing context.)";
    if (score < 60) return "AI: 'To Whom It May Concern, Thank you for applying. We are not moving forward. Best, HR.' (The 'Generic Robot' response. It works, but burns bridges.)";
    if (score < 90) return "AI: 'Dear Candidate, Thank you for your time. While your skills are impressive, we chose another. We will keep your resume on file.' (Better! But a bit clichéd.)";
    
    // 3. THE WIN STATE (90+)
    return "AI: 'Dear [Name], Thank you for investing time in our 2nd round. While we were impressed by your portfolio, we prioritized experience with React Native. We'd love to stay connected for future roles.' (Perfect. Specific, respectful, and leaves the door open.)";
  };

  const handleBlockClick = (block) => {
    if (showSuccess) return;

    // Toggle Logic: If already selected, remove it. If not, add it.
    // CONSTRAINT: Only 1 block per Category allowed to prevent score spamming.
    let newBlocks = [...selectedBlocks];
    const existingIndex = newBlocks.findIndex(b => b.id === block.id);
    const categoryConflictIndex = newBlocks.findIndex(b => b.category === block.category);

    if (existingIndex >= 0) {
      // Remove (Toggle Off)
      newBlocks.splice(existingIndex, 1);
    } else {
      // Add (Toggle On) - Remove conflict if exists
      if (categoryConflictIndex >= 0) {
        newBlocks.splice(categoryConflictIndex, 1);
      }
      newBlocks.push(block);
    }

    // Recalculate Score
    const newScore = 10 + newBlocks.reduce((acc, b) => acc + b.value, 0);
    
    setSelectedBlocks(newBlocks);
    setPromptScore(Math.min(100, Math.max(0, newScore)));
    setFeedback(block.tip); // Show immediate feedback for the last click

    // Win Condition
    if (newScore >= 95) {
      setTimeout(() => setShowSuccess(true), 1000);
    }
  };

  const handleReset = () => {
    setPromptScore(10);
    setSelectedBlocks([]);
    setShowSuccess(false);
    setFeedback("Reset complete. Build your prompt.");
  };

  return (
    <div className="bg-slate-50 p-4 sm:p-6 rounded-2xl border border-slate-200 max-w-3xl mx-auto relative overflow-hidden">
      
      {/* HEADER */}
      <div className="mb-6 flex justify-between items-start">
        <div>
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
            <Sparkles className="text-purple-600" /> Prompt Golf
            </h2>
            <p className="text-slate-600 text-sm mt-1">
            Turn a lazy prompt ("Write rejection") into a professional asset.
            </p>
        </div>
        <div className="text-right">
            <div className="text-3xl font-black text-blue-600">{promptScore}%</div>
            <div className="text-[10px] uppercase font-bold text-slate-400">Quality Score</div>
        </div>
      </div>

      {/* PROMPT BUILDER BAR */}
      <div className="bg-white p-3 rounded-xl border-2 border-dashed border-slate-300 mb-6 min-h-[60px] flex flex-wrap items-center gap-2">
        <span className="text-slate-400 font-bold text-sm select-none">Prompt:</span>
        <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-sm font-mono border">"Write a rejection email..."</span>
        <AnimatePresence>
            {selectedBlocks.map(block => (
                <motion.span 
                    key={block.id}
                    initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                    className={`px-2 py-1 rounded text-xs font-bold border flex items-center gap-1 ${block.type === 'good' ? 'bg-green-100 text-green-700 border-green-200' : block.type === 'meh' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                >
                    {block.category}: {block.type === 'good' ? '✅' : block.type === 'meh' ? '⚠️' : '❌'}
                </motion.span>
            ))}
        </AnimatePresence>
        {selectedBlocks.length === 0 && <span className="text-slate-300 text-sm italic ml-2">...add blocks below...</span>}
      </div>

      {/* AI OUTPUT PREVIEW */}
      <div className="bg-slate-900 p-5 rounded-xl shadow-inner mb-6 relative overflow-hidden transition-all duration-300">
        <div className="absolute top-0 right-0 p-2 opacity-10"><Zap size={48} className="text-white"/></div>
        <div className="text-xs font-bold text-purple-400 mb-2 uppercase tracking-widest flex items-center gap-2">
            <RefreshCw size={12} className={promptScore < 100 ? "animate-spin" : ""} /> Simulated Output
        </div>
        <p className={`font-mono text-sm leading-relaxed ${promptScore > 90 ? 'text-emerald-300' : promptScore > 50 ? 'text-slate-300' : 'text-red-300'}`}>
          {getSimulatedOutput(promptScore, selectedBlocks)}
        </p>
      </div>

      {/* FEEDBACK TOAST */}
      <div className="h-8 mb-4 flex items-center justify-center">
        {selectedBlocks.length > 0 && (
            <span className="text-xs font-bold text-slate-500 bg-white px-3 py-1 rounded-full border shadow-sm animate-fade-in">
                Coach: "{feedback}"
            </span>
        )}
      </div>

      {/* BLOCKS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 mb-6">
        {BLOCK_OPTIONS.map((block) => {
          const isSelected = selectedBlocks.find(b => b.id === block.id);
          const isCategoryFilled = selectedBlocks.find(b => b.category === block.category && b.id !== block.id);
          
          return (
            <button
              key={block.id}
              onClick={() => handleBlockClick(block)}
              disabled={showSuccess || (isCategoryFilled && !isSelected)}
              className={`p-3 text-xs font-bold rounded-xl border-2 text-left flex items-center justify-between transition-all ${
                isSelected 
                  ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-md ring-1 ring-blue-200' 
                  : isCategoryFilled 
                    ? 'opacity-40 bg-gray-50 border-gray-100 grayscale cursor-not-allowed'
                    : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm'
              }`}
            >
              <span>{block.label}</span>
              {isSelected && <CheckCircle size={16} className="text-blue-500" />}
            </button>
          )
        })}
      </div>

      <div className="text-center">
        <button onClick={handleReset} className="text-xs font-bold text-slate-400 hover:text-slate-600 underline">
            Clear Prompt
        </button>
      </div>

      {/* SUCCESS OVERLAY */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute inset-0 bg-white/95 backdrop-blur-md z-20 flex flex-col items-center justify-center p-6 text-center"
          >
            <motion.div 
                initial={{ scale: 0 }} animate={{ scale: 1 }} 
                className="bg-emerald-100 p-6 rounded-full mb-4 shadow-lg"
            >
                <Trophy className="w-16 h-16 text-emerald-600" />
            </motion.div>
            <h3 className="text-3xl font-black text-slate-800 mb-2">Prompt Mastered!</h3>
            <p className="text-slate-600 mb-8 max-w-md text-sm leading-relaxed">
                You took the wheel. By specifying <strong>Role, Context, and Tone</strong>, you turned a generic AI into a precision tool.
            </p>
            <button 
              onClick={() => onComplete(100)} 
              className="w-full max-w-sm py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl flex items-center justify-center gap-2 shadow-xl transition-transform transform hover:scale-105"
            >
              Finish Lesson <ArrowRight size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default PromptGolfGame;